import { DecisionState, Role, UIElement } from '@enums';
import decisionAccessJson from './access-map/decision_access.json';

export type DecisionAccessLevel =
  | 'none'
  | 'read'
  | 'write'
  | 'execute'
  | 'execute_own';

export type DecisionAccessKey =
  | 'fileNumber'
  | 'judge'
  | 'anonymizationRequest'
  | 'anonymize'
  | 'state'
  | 'number'
  | 'form'
  | 'settlementMethods'
  | 'allowPublishing'
  | 'takeOverIndexes'
  | 'decisionDate'
  | 'publicationDate'
  | 'firstMadeAccessibleDate'
  | 'lastMadeAccessibleDate'
  | 'publishInCollection'
  | 'parallelCitationSb'
  | 'parallelCitationSbnu'
  | 'popularName'
  | 'importance'
  | 'differentOpinion'
  | 'note'
  | 'language'
  | 'excludedFromNalus'
  | 'notes'
  | 'fullText'
  | 'anonymizedText'
  | 'decisionFullText'
  | 'decisionPublishedText'
  | 'decisionAfterCorrection'
  | 'otherAnnouncement'
  | 'history'
  | 'toIndex_op'
  | 'toAnonymization_op'
  | 'toMakeAccessible_op'
  | 'regenerateAttachments_op'
  | 'cancel_op'
  | 'update_op'
  | 'toInProgress_op'
  | 'publish_op';

export type DecisionAccessStateMap = Partial<
  Record<DecisionAccessKey, DecisionAccessLevel>
>;

export type DecisionAccessRoleMap = Partial<
  Record<DecisionState, DecisionAccessStateMap>
>;

export type DecisionAccessMap = Partial<Record<Role, DecisionAccessRoleMap>>;

type RawDecisionAccessMap = Record<
  string,
  Record<string, Record<string, DecisionAccessLevel>>
>;

const roleByAccessKey: Record<string, Role> = {
  lustrum: Role.LUSTRUM,
  head_osv: Role.HEAD_OSV,
  index: Role.INDEX,
  lawyer: Role.LAWYER,
  secretary: Role.SECRETARY,
  judge: Role.JUDGE,
  assistant: Role.ASSISTANT,
  file_room: Role.FILE_ROOM,
  analyst: Role.ANALYST,
  anonymization: Role.ANONYMIZATION,
  publication: Role.PUBLICATION,
  reader: Role.READER,
  adviser: Role.ADVISER,
};

const decisionStateByAccessKey: Record<string, DecisionState> = {
  in_progress: DecisionState.IN_PROGRESS,
  indexing: DecisionState.INDEXING,
  to_anonymize: DecisionState.TO_ANONYMIZE,
  to_make_accessible: DecisionState.TO_MAKE_ACCESSIBLE,
  accessible: DecisionState.ACCESSIBLE,
  canceled: DecisionState.CANCELED,
};

export function isReadableAccess(level?: DecisionAccessLevel): boolean {
  return ['read', 'write', 'execute', 'execute_own'].includes(level ?? 'none');
}

export function isWritableAccess(level?: DecisionAccessLevel): boolean {
  return level === 'write';
}

export function isExecutableAccess(level?: DecisionAccessLevel): boolean {
  return level === 'execute' || level === 'execute_own';
}

export const decisionElementToAccessKey: Partial<
  Record<UIElement.Decision, DecisionAccessKey>
> = {
  [UIElement.Decision.FILE_NUMBER]: 'fileNumber',
  [UIElement.Decision.JUDGE]: 'judge',
  [UIElement.Decision.ANONYMIZATION_REQUEST]: 'anonymizationRequest',
  [UIElement.Decision.ANONYMIZE]: 'anonymize',
  [UIElement.Decision.STATE]: 'state',
  [UIElement.Decision.NUMBER]: 'number',
  [UIElement.Decision.FORM]: 'form',
  [UIElement.Decision.SETTLEMENT_METHODS]: 'settlementMethods',
  [UIElement.Decision.ALLOW_PUBLISHING]: 'allowPublishing',
  [UIElement.Decision.TAKE_OVER_INDEXES]: 'takeOverIndexes',
  [UIElement.Decision.DECISION_DATE]: 'decisionDate',
  [UIElement.Decision.PUBLICATION_DATE]: 'publicationDate',
  [UIElement.Decision.FIRST_MADE_ACCESSIBLE_DATE]: 'firstMadeAccessibleDate',
  [UIElement.Decision.LAST_MADE_ACCESSIBLE_DATE]: 'lastMadeAccessibleDate',
  [UIElement.Decision.PUBLISH_IN_COLLECTION]: 'publishInCollection',
  [UIElement.Decision.PARALLEL_CITATION_SB]: 'parallelCitationSb',
  [UIElement.Decision.PARALLEL_CITATION_SBNU]: 'parallelCitationSbnu',
  [UIElement.Decision.POPULAR_NAME]: 'popularName',
  [UIElement.Decision.IMPORTANCE]: 'importance',
  [UIElement.Decision.DIFFERENT_OPINION]: 'differentOpinion',
  [UIElement.Decision.NOTE]: 'note',
  [UIElement.Decision.LANGUAGE]: 'language',
  [UIElement.Decision.EXCLUDED_FROM_NALUS]: 'excludedFromNalus',
  [UIElement.Decision.NOTES]: 'notes',
  [UIElement.Decision.TEXT]: 'fullText',
  [UIElement.Decision.ANONYMIZED_TEXT]: 'anonymizedText',
  [UIElement.Decision.FULL_TEXT]: 'decisionFullText',
  [UIElement.Decision.PUBLIC_TEXT]: 'decisionPublishedText',
  [UIElement.Decision.CORRECTED_TEXT]: 'decisionAfterCorrection',
  [UIElement.Decision.OTHER_ANNOUNCEMENTS]: 'otherAnnouncement',
  [UIElement.Decision.HISTORY]: 'history',
  [UIElement.Decision.INDEXING]: 'toIndex_op',
  [UIElement.Decision.TO_ANONYMIZE]: 'toAnonymization_op',
  [UIElement.Decision.TO_MAKE_ACCESSIBLE]: 'toMakeAccessible_op',
  [UIElement.Decision.REGENERATE_ATTACHMENTS]: 'regenerateAttachments_op',
  [UIElement.Decision.CANCEL_ACCESS]: 'cancel_op',
  [UIElement.Decision.IN_PROGRESS]: 'toInProgress_op',
  [UIElement.Decision.ACCESSIBLE]: 'publish_op',
  [UIElement.Decision.EDIT_DECISION_BUTTON]: 'update_op',
};

function normalizeDecisionAccessMap(
  rawMap: RawDecisionAccessMap
): DecisionAccessMap {
  return Object.entries(rawMap).reduce<DecisionAccessMap>(
    (roleMap, [rawRole, rawStateMap]) => {
      const role = roleByAccessKey[rawRole];

      if (!role) {
        return roleMap;
      }

      roleMap[role] = Object.entries(rawStateMap).reduce<DecisionAccessRoleMap>(
        (stateMap, [rawState, rawAccessMap]) => {
          const state = decisionStateByAccessKey[rawState];

          if (!state) {
            return stateMap;
          }

          stateMap[state] = rawAccessMap as DecisionAccessStateMap;

          return stateMap;
        },
        {}
      );

      return roleMap;
    },
    {}
  );
}

// Statická FE access mapa používá lokální kopii BE zdroje pravdy:
// ./access-map/decision_access.json.
export const decisionAccessMap: DecisionAccessMap = normalizeDecisionAccessMap(
  decisionAccessJson as RawDecisionAccessMap
);
